import { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useSnackbar } from 'notistack';

import { RootState } from '@/store/store';
import { ModerationState } from '@/types/service-management.types';
import { ErrorApiResponse } from '@/types/api.types';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetServicesQuery,
  useDeleteServiceMutation,
  useToggleServiceVisibilityMutation,
  useSubmitServiceMutation,
  useWithdrawServiceMutation,
} from '@/services/ServiceManagementService';

const formatCategoryDisplay = (category: string): string => {
  if (!category) return 'N/A';
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ServiceProviderDashboard = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    serviceId: string | null;
  }>({ isOpen: false, serviceId: null });

  const [deleteService] = useDeleteServiceMutation();
  const [toggleServiceVisibility] = useToggleServiceVisibilityMutation();
  const [submitService] = useSubmitServiceMutation();
  const [withdrawService] = useWithdrawServiceMutation();

  const userData = useSelector((state: RootState) => state.auth.userData);
  const selectedServiceProviderId = useSelector(
    (state: RootState) => state.auth.selectedServiceProviderId
  );

  const serviceProviderId = selectedServiceProviderId || userData?.id || null;

  const handleOpenService = useCallback(
    (service: { id?: string | null } | null, mode: 'view' | 'edit') => {
      if (!service?.id) return;

      if (mode === 'view') {
        navigate(`/services/view/${service.id}`);
      } else {
        navigate(`/services/edit/${service.id}`);
      }
    },
    [navigate]
  );

  const handleCreateService = useCallback(() => {
    const params = new URLSearchParams();
    if (serviceProviderId) {
      params.append('serviceProviderId', serviceProviderId);
    }
    navigate(`/services/create?${params.toString()}`);
  }, [navigate, serviceProviderId]);

  const handleSubmit = async (serviceId: string) => {
    try {
      await submitService(serviceId).unwrap();
      enqueueSnackbar('Service submitted for moderation', {
        variant: 'success',
      });
      if (typeof refetchServices === 'function') {
        refetchServices();
      }
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleWithdraw = async (serviceId: string) => {
    try {
      await withdrawService(serviceId).unwrap();
      enqueueSnackbar('Service withdrawn successfully', { variant: 'success' });
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleToggleVisibility = async (
    serviceId: string,
    currentVisibility: boolean
  ) => {
    const newVisibility = !currentVisibility;
    try {
      await toggleServiceVisibility({
        serviceId,
        is_visible: newVisibility,
      }).unwrap();
      enqueueSnackbar(
        `Service ${newVisibility ? 'shown' : 'hidden'} successfully`,
        { variant: 'success' }
      );
      if (typeof refetchServices === 'function') {
        refetchServices();
      }
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.serviceId) return;
    try {
      await deleteService(confirmDeleteState.serviceId).unwrap();
      enqueueSnackbar('Service deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, serviceId: null });
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteState({ isOpen: false, serviceId: null });
    }
  };

  const {
    data: servicesResponse,
    isLoading: isServicesLoading,
    isError: isServicesError,
    refetch: refetchServices,
  } = useGetServicesQuery(
    {
      page: 1,
      limit: 50,
      service_provider_id: serviceProviderId || undefined,
    },
    {
      skip: !serviceProviderId,
    }
  );

  const serviceStats = useMemo(() => {
    const data = servicesResponse?.data || [];

    const total = servicesResponse?.totalItems ?? data.length;
    let draft = 0;
    let submitted = 0;
    let approved = 0;
    let rejected = 0;
    let disabled = 0;
    let visible = 0;

    data.forEach((service) => {
      if (service.public_visibility) {
        visible += 1;
      }

      switch (service.moderation_state) {
        case ModerationState.DRAFT:
          draft += 1;
          break;
        case ModerationState.SUBMITTED:
          submitted += 1;
          break;
        case ModerationState.APPROVED:
          approved += 1;
          break;
        case ModerationState.REJECTED:
          rejected += 1;
          break;
        case ModerationState.DISABLED:
          disabled += 1;
          break;
        default:
          break;
      }
    });

    return {
      total,
      draft,
      submitted,
      approved,
      rejected,
      disabled,
      visible,
    };
  }, [servicesResponse]);

  if (!serviceProviderId) {
    return (
      <Container>
        <div className="card card-grid">
          <div className="card-body">
            <p className="text-sm text-gray-600">
              No service provider is currently selected. Please make sure a
              service provider account is associated with this user.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  if (isServicesError) {
    return (
      <Container>
        <div className="card card-grid">
          <div className="card-body">
            <p className="text-sm text-red-600">
              Failed to load dashboard data. Please try again later.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  const isLoading = isServicesLoading;

  return (
    <Container className="p-0">
      <section
        className="space-y-6"
        aria-label="Service provider dashboard overview"
      >
        {!isLoading && serviceStats.total === 0 && (
          <div
            className="flex flex-col md:flex-row items-center gap-6 p-6 md:p-8 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-800 dark:to-slate-900 border border-indigo-100 dark:border-slate-700 rounded-xl shadow-sm mb-6"
            role="alert"
          >
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 bg-indigo-500 rounded-full text-white shadow-md">
              <KeenIcon type="filled" icon="information-3" className="text-2xl text-white" />
            </div>
            <div className="flex-grow space-y-2 text-center md:text-left">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Get Started: Set Up Your First Service
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 max-w-2xl leading-relaxed">
                Your Service Provider profile is active, but you haven't added any services yet. 
                Service Providers must create at least one service listing (e.g., equipment rental, kiosk, harbor guiding, boat maintenance) for boaters to see and book your offerings.
              </p>
            </div>
            <div className="flex-shrink-0 w-full md:w-auto">
              <button
                type="button"
                onClick={handleCreateService}
                className="btn btn-primary w-full md:w-auto py-3 px-6 font-semibold flex items-center justify-center gap-2 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              >
                <KeenIcon type="filled" icon="plus" className="text-lg" />
                Add Your First Service
              </button>
            </div>
          </div>
        )}

        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"
          role="region"
          aria-label="Service statistics"
        >
          <DashboardStatCard
            title="Total Services"
            value={serviceStats.total}
            description="All services you have created"
            icon="category"
            loading={isLoading}
          />
          <DashboardStatCard
            title="Published Services"
            value={serviceStats.visible}
            description="Approved and visible to end users"
            icon="tick-circle"
            loading={isLoading}
          />
          <DashboardStatCard
            title="In Review"
            value={serviceStats.submitted}
            description="Submitted and awaiting moderation"
            icon="shield-search"
            loading={isLoading}
          />
          <DashboardStatCard
            title="Needs Changes"
            value={serviceStats.draft + serviceStats.rejected}
            description="Draft or rejected services"
            icon="danger"
            loading={isLoading}
          />
        </div>

        <div
          className="grid grid-cols-1 gap-6 lg:grid-cols-1"
          role="region"
          aria-labelledby="recent-services-heading recent-services-description"
        >
          <div className="lg:col-span-2">
            <div className="card card-grid h-full rounded-lg border border-gray-500 text-gray-900 shadow-md p-2">
              <div className="card-header flex items-center justify-between">
                <div>
                  <h3
                    id="recent-services-heading"
                    className="card-title text-base font-semibold text-gray-900"
                  >
                    Recent Services
                  </h3>
                  <p
                    id="recent-services-description"
                    className="text-xs text-gray-500"
                  >
                    Snapshot of your latest services with their moderation
                    status.
                  </p>
                </div>
              </div>
              <div className="card-body overflow-x-auto">
                {isServicesLoading ? (
                  <p className="text-sm text-gray-600">Loading services...</p>
                ) : (servicesResponse?.data || []).length === 0 ? (
                  <p className="text-sm text-gray-600">
                    You have not created any services yet.
                  </p>
                ) : (
                  <table
                    className="table table-zebra w-full text-sm"
                    aria-labelledby="recent-services-heading recent-services-description"
                  >
                    <caption className="sr-only">
                      Recent services created by this service provider with
                      their moderation status, public visibility and available
                      actions.
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" className="font-medium text-gray-600">
                          Service Name
                        </th>
                        <th scope="col" className="font-medium text-gray-600">
                          Service Provider
                        </th>
                        <th scope="col" className="font-medium text-gray-600">
                          Category
                        </th>
                        <th scope="col" className="font-medium text-gray-600">
                          Sub Category
                        </th>
                        <th scope="col" className="font-medium text-gray-600">
                          Type
                        </th>
                        <th scope="col" className="font-medium text-gray-600">
                          Moderation Status
                        </th>
                        <th scope="col" className="font-medium text-gray-600">
                          Public Visibility
                        </th>
                        <th scope="col" className="font-medium text-gray-600">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(servicesResponse?.data || [])
                        .slice(0, 5)
                        .map((service) => (
                          <tr key={service.id}>
                            <td
                              className="max-w-xs truncate"
                              title={service.name}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  handleOpenService(service, 'view')
                                }
                                className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label={`View ${service.name}`}
                              >
                                {service.name}
                              </button>
                            </td>
                            <td>
                              <span className="text-gray-800 font-normal">
                                {
                                  service.service_provider
                                    ?.display_name as string
                                }
                              </span>
                            </td>
                            <td>
                              <span className="text-gray-800 font-normal">
                                {formatCategoryDisplay(
                                  service.category as string
                                )}
                              </span>
                            </td>
                            <td>
                              <span className="text-gray-800 font-normal">
                                {service.sub_category || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className="text-gray-800 font-normal">
                                {service.service_type || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className="px-3 py-1.5 rounded-full border text-xs font-bold min-w-[110px] inline-block text-center bg-gray-100 dark:bg-coal-600 text-gray-700 dark:text-gray-light-700 border-gray-200 dark:border-coal-100">
                                {renderModerationBadge(
                                  service.moderation_state
                                )}
                              </span>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleToggleVisibility(
                                      service.id as string,
                                      !!service.public_visibility
                                    )
                                  }
                                  className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                                    service.public_visibility
                                      ? 'bg-green-500 hover:bg-green-600'
                                      : 'bg-gray-300 hover:bg-gray-400'
                                  }`}
                                  role="switch"
                                  disabled={
                                    service.moderation_state !==
                                    ModerationState.APPROVED
                                  }
                                  aria-checked={!!service.public_visibility}
                                  title={
                                    service.public_visibility
                                      ? 'Click to hide'
                                      : 'Click to show'
                                  }
                                  aria-label={
                                    service.public_visibility
                                      ? 'Hide service'
                                      : 'Show service'
                                  }
                                >
                                  <span className="sr-only">
                                    Toggle visibility
                                  </span>
                                  <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition-transform duration-200 ease-in-out ${
                                      service.public_visibility
                                        ? 'translate-x-6'
                                        : 'translate-x-0.5'
                                    }`}
                                  />
                                </button>
                                <span
                                  className={`text-sm font-medium ${
                                    service.public_visibility
                                      ? 'text-green-700'
                                      : 'text-gray-600'
                                  }`}
                                >
                                  {service.public_visibility
                                    ? 'Visible'
                                    : 'Hidden'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div
                                className="flex items-center gap-2"
                                role="group"
                                aria-label={`Actions for ${service.name}`}
                              >
                                {hasPermission('service_management_read') && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleOpenService(service, 'view')
                                    }
                                    className="btn btn-xs btn-icon btn-light btn-outline"
                                    title="View service"
                                    aria-label={`View ${service.name}`}
                                  >
                                    <KeenIcon
                                      type="filled"
                                      icon="eye"
                                      className="text-gray-500"
                                      aria-hidden="true"
                                    />
                                  </button>
                                )}
                                {hasPermission('service_management_update') &&
                                  service.moderation_state !==
                                    ModerationState.SUBMITTED && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleOpenService(service, 'edit')
                                      }
                                      className="btn btn-xs btn-icon btn-light btn-outline"
                                      title="Edit service"
                                      aria-label={`Edit ${service.name}`}
                                    >
                                      <KeenIcon
                                        type="filled"
                                        icon="pencil"
                                        className="text-gray-500"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                                {(service.moderation_state ===
                                  ModerationState.DRAFT ||
                                  service.moderation_state ===
                                    ModerationState.REJECTED) &&
                                  hasPermission(
                                    'service_management_update'
                                  ) && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleSubmit(service.id as string)
                                      }
                                      className="btn btn-xs btn-icon btn-light btn-outline"
                                      title="Submit for moderation"
                                      aria-label={`Submit ${service.name}`}
                                    >
                                      <KeenIcon
                                        type="filled"
                                        icon="rocket"
                                        className="text-blue-500"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                                {service.moderation_state ===
                                  ModerationState.SUBMITTED &&
                                  hasPermission(
                                    'service_management_update'
                                  ) && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleWithdraw(service.id as string)
                                      }
                                      className="btn btn-xs btn-icon btn-light btn-outline"
                                      title="Withdraw submission"
                                      aria-label={`Withdraw ${service.name}`}
                                    >
                                      <KeenIcon
                                        type="filled"
                                        icon="cross-circle"
                                        className="text-orange-500"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                                {hasPermission('service_management_delete') &&
                                  service.moderation_state !==
                                    ModerationState.SUBMITTED && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setConfirmDeleteState({
                                          isOpen: true,
                                          serviceId: service.id as string,
                                        })
                                      }
                                      className="btn btn-xs btn-icon btn-light btn-outline"
                                      title="Delete service"
                                      aria-label={`Delete ${service.name}`}
                                    >
                                      <KeenIcon
                                        type="filled"
                                        icon="trash"
                                        className="text-red-500"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ConfirmationDialog
        open={confirmDeleteState.isOpen}
        onClose={() =>
          setConfirmDeleteState({ isOpen: false, serviceId: null })
        }
        onConfirm={handleDelete}
        message="Are you sure you want to delete this service?"
        cancelButtonText="No"
        confirmButtonText="Yes"
      />
    </Container>
  );
};

const renderModerationBadge = (state?: ModerationState) => {
  switch (state) {
    case ModerationState.APPROVED:
      return 'Approved';
    case ModerationState.SUBMITTED:
      return 'In Review';
    case ModerationState.DRAFT:
      return 'Draft';
    case ModerationState.REJECTED:
      return 'Rejected';
    case ModerationState.DISABLED:
      return 'Disabled';
    default:
      return 'Unknown';
  }
};

export { ServiceProviderDashboard };
